import {
  Box,
  createStyles,
  Group,
  ScrollArea,
  Tabs,
  TabsProps,
} from '@mantine/core';
import { find } from 'lodash/fp';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  DeviceModelType as OrgDeviceModelType,
  DeviceType,
} from '@portals/api/organizations';
import { DeviceModelSummaryType as PartnerDeviceModelType } from '@portals/api/partners';
import { VerticalScrollBar } from '@portals/scrollbar';
import { DeviceType as CommonDeviceType, TabType } from '@portals/types';

import { useDeviceContext } from './context';
import { TOUR_STEPS_IDS } from '../../../constants/setup/setup.constants';
import { useRouteModalConfig } from '../../route-modals.hooks';

interface DeviceTabsProps<TDevice, TDeviceModel> {
  device: TDevice;
  model: TDeviceModel;
}

export function DeviceTabs<
  TDevice extends DeviceType | CommonDeviceType,
  TDeviceModel extends OrgDeviceModelType | PartnerDeviceModelType
>({ device, model }: DeviceTabsProps<TDevice, TDeviceModel>) {
  const { classes } = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ tab_id?: string }>();
  const routeModalConfig = useRouteModalConfig('device');
  const deviceContext = useDeviceContext();

  const [activeTabId, setActiveTabId] = useState(
    params.tab_id || deviceContext.tabs[0].id
  );

  useEffect(
    function updateRouteWithInitialTabId() {
      if (!params.tab_id) {
        navigate(routeModalConfig.generatePath([device.id, activeTabId]), {
          replace: true,
          state: location.state,
        });
      }

      const tab = find({ id: params.tab_id }, deviceContext.tabs);
      if (!tab) {
        navigate(
          routeModalConfig.generatePath([device.id, deviceContext.tabs[0].id]),
          {
            replace: true,
            state: location.state,
          }
        );
      }
    },
    [
      activeTabId,
      device.id,
      navigate,
      params.tab_id,
      routeModalConfig,
      deviceContext.tabs,
      location.state,
    ]
  );

  useEffect(
    function handleNativeRoutingActions() {
      // update active tabId when actions like 'back' & 'forward' occur
      if (params.tab_id && params.tab_id !== activeTabId) {
        setActiveTabId(params.tab_id);
      }
    },
    [activeTabId, params.tab_id]
  );

  const onTabChange = (value: string) => {
    if (value === activeTabId) return;

    setActiveTabId(value);
    navigate(routeModalConfig.generatePath([device.id, value]), {
      state: location.state,
    });
  };

  const tabLabelRenderer = ({
    title,
    Counter,
    counterProps,
  }: Pick<TabType, 'title' | 'Counter' | 'counterProps'>) =>
    Counter ? (
      <Group spacing="xs" noWrap>
        {title}
        <Counter {...counterProps?.(device)} />
      </Group>
    ) : (
      title
    );

  return (
    <div className={classes.container}>
      <Tabs
        keepMounted={false}
        styles={tabsStyles}
        value={activeTabId}
        onTabChange={onTabChange}
        data-tour-step-id={TOUR_STEPS_IDS.deviceOverview}
      >
        <ScrollArea type="auto">
          <Tabs.List grow>
            {(deviceContext.tabs || []).map(
              ({ id, title, Counter, counterProps }) => (
                <Tabs.Tab key={id} value={id} data-testid={`device-tab-${id}`}>
                  {tabLabelRenderer({ title, Counter, counterProps })}
                </Tabs.Tab>
              )
            )}
          </Tabs.List>
        </ScrollArea>

        {(deviceContext.tabs || []).map(({ id, Component }) => (
          <Tabs.Panel key={id} value={id}>
            <VerticalScrollBar renderView={(props) => <Box {...props} />}>
              <Component device={device} model={model} />
            </VerticalScrollBar>
          </Tabs.Panel>
        ))}
      </Tabs>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    height: '100%',
    width: '100%',
    background: theme.colors.gray[0],
    padding: 30,
    paddingBottom: 0,
  },
}));

const tabsStyles: TabsProps['styles'] = (theme) => ({
  root: {
    height: '100%',
    display: 'grid',
    gridTemplateRows: 'max-content 1fr',
  },
  tabsList: {
    paddingRight: theme.spacing.xl,
    flexWrap: 'nowrap',
    borderWidth: 3,
  },
  tab: {
    height: 37,
    marginBottom: -3,
    borderWidth: 3,

    [`${theme.fn.smallerThan(1550)}`]: {
      padding: theme.spacing.xs,
    },
  },

  tabLabel: {
    color: theme.colors.gray[5],
    fontWeight: 400,
  },
});
